import React from 'react';
import styled from 'styled-components';
import { animated } from '@react-spring/web';
import { ClubCard as ClubCardComponent, ClubCardSkeleton } from '../ClubCard';
import { LoadMoreTrigger } from '../LoadMoreTrigger';

const ClubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const AnimatedCard = styled(animated.div)`
  will-change: opacity, transform;
`;

export interface Club {
  id: string;
  name: string;
  slug: string;
  country: string;
  disabled?: boolean;
}

export interface ClubsGridProps {
  clubs: Club[];
  loadedClubIds: Set<string>;
  onClubSelect: (clubSlug: string, disabled?: boolean) => void;
  hasMorePages?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export const ClubsGrid: React.FC<ClubsGridProps> = ({
  clubs,
  loadedClubIds,
  onClubSelect,
  hasMorePages = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  return (
    <>
      <ClubGrid>
        {clubs.map((club) => {
          const isLoaded = loadedClubIds.has(club.id);

          return (
            <AnimatedCard
              key={club.id}
              style={{
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0px) scale(1)' : 'translateY(20px) scale(0.95)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {isLoaded ? (
                <ClubCardComponent
                  id={club.id}
                  name={club.name}
                  slug={club.slug}
                  country={club.country}
                  disabled={club.disabled}
                  data-club-slug={club.slug}
                  onClick={() => onClubSelect(club.slug, club.disabled)}
                  onButtonClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    onClubSelect(club.slug, club.disabled);
                  }}
                />
              ) : (
                <ClubCardSkeleton data-club-slug={club.slug} />
              )}
            </AnimatedCard>
          );
        })}
      </ClubGrid>

      {/* Infinite scroll trigger */}
      {hasMorePages && onLoadMore && (
        <LoadMoreTrigger onLoadMore={onLoadMore} isLoading={isLoadingMore} />
      )}
    </>
  );
};
